import { apiClient, newIdempotencyKey } from './client'
import type { CursorPage } from './accounts'
import type { CreateTaskRequest, TaskStatus, TaskView, UpdateTaskRequest } from '../types/task'

/**
 * Typed fetch wrappers for `module.task`'s `Task` surface
 * (`TaskController`/`CaseTaskController`) — same thin-wrapper convention as
 * `api/cases.ts`/`api/caseTeam.ts`. Backs `screens/tasks/MyWorkScreen.tsx`
 * and `screens/cases/TasksPanel.tsx`.
 */

export interface ListTasksParams {
  /** RSQL, already combined by the caller (see lib/rsql.ts). */
  filter?: string | null
  sort?: string | null
  cursor?: string | null
  limit?: number
}

function toQuery(params: ListTasksParams): string {
  const q = new URLSearchParams()
  if (params.filter) q.set('filter', params.filter)
  if (params.sort) q.set('sort', params.sort)
  if (params.cursor) q.set('cursor', params.cursor)
  q.set('limit', String(params.limit ?? 50))
  return q.toString()
}

/** `GET /tasks` — the cross-case "My Work" read. */
export async function listTasks(params: ListTasksParams, signal?: AbortSignal): Promise<CursorPage<TaskView>> {
  const { data } = await apiClient.get<CursorPage<TaskView>>(`/tasks?${toQuery(params)}`, { signal })
  return data
}

/** `GET /cases/{caseId}/tasks` — the Case "Tasks" tab's data source. */
export async function listCaseTasks(
  caseId: string,
  cursor?: string | null,
  signal?: AbortSignal,
): Promise<CursorPage<TaskView>> {
  const q = new URLSearchParams()
  if (cursor) q.set('cursor', cursor)
  q.set('limit', '200')
  const { data } = await apiClient.get<CursorPage<TaskView>>(`/cases/${caseId}/tasks?${q.toString()}`, { signal })
  return data
}

export async function getTask(id: string, signal?: AbortSignal): Promise<TaskView> {
  const { data } = await apiClient.get<TaskView>(`/tasks/${id}`, { signal })
  return data
}

/** `POST /tasks` — standalone create; `caseId` optional in the body. */
export async function createTask(request: CreateTaskRequest): Promise<TaskView> {
  const { data } = await apiClient.post<TaskView>('/tasks', request, { idempotencyKey: newIdempotencyKey() })
  return data
}

/** `POST /cases/{caseId}/tasks` — case-scoped create; the path `caseId` always wins over the body. */
export async function createCaseTask(
  caseId: string,
  request: Omit<CreateTaskRequest, 'caseId'>,
): Promise<TaskView> {
  const { data } = await apiClient.post<TaskView>(`/cases/${caseId}/tasks`, request, {
    idempotencyKey: newIdempotencyKey(),
  })
  return data
}

/**
 * `PATCH /tasks/{id}` — full replace of the mutable field set, never `status`. No `If-Match`
 * enforcement on this route (mirrors `CaseTeamMemberController`'s deactivate PATCH — optimistic
 * concurrency via `If-Match` is opt-in per route in this codebase, not universal).
 */
export async function updateTask(id: string, request: UpdateTaskRequest): Promise<TaskView> {
  const { data } = await apiClient.patch<TaskView>(`/tasks/${id}`, request)
  return data
}

/** `POST /tasks/{id}/transitions` — the only path that changes `status`. */
export async function transitionTaskStatus(id: string, toStatus: TaskStatus): Promise<TaskView> {
  const { data } = await apiClient.post<TaskView>(`/tasks/${id}/transitions`, { toStatus })
  return data
}
